// src/views/ScreenerView.js
import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, ListFilter, Flame, ArrowUpDown, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { API_BASE } from '../api/db';

function CuratedRow({ ticker, name, isEtf, setCurrentRoute, setGlobalSearchTicker, themeClasses, stats }) {
  const price = stats?.price ?? null;
  const yieldVal = stats?.yield ?? null;
  const pe = stats?.pe ?? null;
  const payout = stats?.payout ?? null;

  return (
    <tr className={`border-b ${themeClasses.border} hover:${isEtf ? 'bg-emerald-500/10' : 'bg-indigo-500/10'} transition-colors`}>
      <td className={`py-2 px-2 font-black ${isEtf ? 'text-emerald-500' : 'text-indigo-500'} text-xs`}>{ticker}</td>
      {name !== undefined && <td className={`py-2 px-2 font-bold ${themeClasses.textHead} truncate max-w-[120px]`}>{name}</td>}
      
      <td className={`py-2 px-2 font-mono text-[10px] ${themeClasses.textMuted}`}>
        {price === null ? <Loader2 size={10} className="animate-spin inline opacity-50"/> : (price === "---" ? "---" : `$${price.toFixed(2)}`)}
      </td>
      
      <td className={`py-2 px-2 font-mono text-[10px] font-bold ${isEtf ? 'text-emerald-400' : 'text-indigo-400'}`}>
        {yieldVal === null ? <Loader2 size={10} className="animate-spin inline opacity-50"/> : yieldVal}
      </td>

      {!isEtf && (
        <>
          <td className={`py-2 px-2 font-mono text-[10px] ${themeClasses.textMuted}`}>
            {pe === null ? <Loader2 size={10} className="animate-spin inline opacity-50"/> : pe}
          </td>
          <td className={`py-2 px-2 font-mono text-[10px] ${themeClasses.textMuted}`}>
            {payout === null ? <Loader2 size={10} className="animate-spin inline opacity-50"/> : payout}
          </td>
        </>
      )}
      
      <td className="py-2 px-2 text-right">
        <button 
          onClick={() => { setGlobalSearchTicker(ticker); setCurrentRoute('dashboard'); }} 
          className={`px-3 py-1 ${isEtf ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500' : 'bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500'} hover:text-white rounded transition-colors font-bold uppercase tracking-widest text-[9px]`}
        >
          Deep Dive
        </button>
      </td>
    </tr>
  );
}

export default function ScreenerView({ themeClasses, setCurrentRoute, setGlobalSearchTicker }) {
  const [masterDirectory, setMasterDirectory] = useState([]);
  
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL"); // ALL, HOT, BAD, VOLUME
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'volume', direction: 'desc' });
  
  const [showCurated, setShowCurated] = useState(false);
  const [curatedStats, setCuratedStats] = useState({});
  const [kingsSort, setKingsSort] = useState({ key: 'ticker', direction: 'asc' });

  const DIVIDEND_KINGS = ["MGEE", "ADP", "MCD", "APD", "RLI", "RPM", "GRC", "NUE", "ED", "MSEX", "WMT", "PEP", "BDX", "SPGI", "TNC", "ABBV", "ADM", "LOW", "KMB", "ABT", "PPG", "TGT", "GWW", "NFG", "MSA", "BKH", "UVV", "ITW", "MO", "SCL", "SYY", "CBSH", "FUL", "SWK", "FRT", "ABM", "CWT", "HRL", "NDSN", "CL", "JNJ", "KO", "CINF", "EMR", "DOV", "NWN", "GPC", "PH", "PG", "AWR"];
  const HIGH_YIELD_ETFS = [{ ticker: "SCHD", name: "Schwab US Dividend Equity" }, { ticker: "VYM", name: "Vanguard High Dividend Yield" }, { ticker: "NOBL", name: "ProShares S&P 500 Aristocrats" }];

  useEffect(() => {
    setLoading(true);
    fetch('/directory.json')
      .then(res => res.json())
      .then(data => { setMasterDirectory(data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
    setSortConfig({ key, direction });
  };

  const filteredAndSortedTickers = useMemo(() => {
    let filtered = masterDirectory;
    
    // UPDATED: Hot is now defined as breaking out (within 3% of 52W High) with healthy liquid volume
    if (activeFilter === "HOT") {
        filtered = filtered.filter(t => t.price >= 5 && t.volume >= 1000000 && t.fiftyTwoWeekHigh && t.price >= (t.fiftyTwoWeekHigh * 0.97));
    } 
    else if (activeFilter === "BAD") {
        filtered = filtered.filter(t => t.price >= 10 && t.change_pct <= -2 && (t.price * t.volume) >= 50000000);
    } 
    else if (activeFilter === "VOLUME") {
        filtered = filtered.filter(t => t.price >= 5 && (t.price * t.volume) >= 100000000);
    }

    if (search.trim()) {
        const lowerSearch = search.toLowerCase();
        filtered = filtered.filter(t => (t.ticker && t.ticker.toLowerCase().includes(lowerSearch)) || (t.name && t.name.toLowerCase().includes(lowerSearch)));
    }

    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || 0;
        const bVal = b[sortConfig.key] || 0;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered.slice(0, 200);
  }, [masterDirectory, search, activeFilter, sortConfig]);

  useEffect(() => {
    if (!showCurated) return;
    let isMounted = true;
    const fetchCuratedDataSequentially = async () => {
      const allCurated = [...DIVIDEND_KINGS, ...HIGH_YIELD_ETFS.map(e => e.ticker)];
      const baseUrl = `${window.location.origin}${API_BASE}`;
      for (const ticker of allCurated) {
        if (!isMounted) break;
        if (curatedStats[ticker]) continue;
        try {
          const isEtf = HIGH_YIELD_ETFS.some(e => e.ticker === ticker);
          const [massiveRes, yfRes] = await Promise.allSettled([
            fetch(`${baseUrl}/massive/v2/snapshot/locale/us/markets/stocks/tickers/${ticker}`).then(r => r.json()),
            fetch(`${baseUrl}/company/${ticker}`).then(r => r.json())
          ]);
          let fetchedPrice = "---", fetchedYield = "N/A", fetchedPe = "N/A", fetchedPayout = "N/A";
          if (massiveRes.status === 'fulfilled' && massiveRes.value.ticker) fetchedPrice = massiveRes.value.ticker.lastTrade?.p || massiveRes.value.ticker.day?.c || massiveRes.value.ticker.prevDay?.c || 0;
          if (yfRes.status === 'fulfilled' && yfRes.value) {
             fetchedYield = isEtf ? (yfRes.value.secYield || "N/A") : (yfRes.value.divYield || "N/A");
             fetchedPe = yfRes.value.pe || "N/A";
             fetchedPayout = isEtf ? "N/A" : (yfRes.value.payoutRatio || "N/A");
          }
          setCuratedStats(prev => ({ ...prev, [ticker]: { price: fetchedPrice, yield: fetchedYield, pe: fetchedPe, payout: fetchedPayout } }));
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (e) {}
      }
    };
    fetchCuratedDataSequentially();
    return () => { isMounted = false; };
  }, [showCurated]);

  const handleKingsSort = (key) => {
    let direction = 'desc'; 
    if (kingsSort.key === key && kingsSort.direction === 'desc') direction = 'asc';
    setKingsSort({ key, direction });
  };

  const sortedKings = useMemo(() => {
    let sortable = [...DIVIDEND_KINGS];
    sortable.sort((a, b) => {
      if (kingsSort.key === 'ticker') return kingsSort.direction === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
      const statsA = curatedStats[a] || {}, statsB = curatedStats[b] || {};
      const parseVal = (val) => {
        if (!val || val === "N/A" || val === "---") return -1;
        return parseFloat(String(val).replace(/[^0-9.-]+/g,""));
      };
      const valA = parseVal(statsA[kingsSort.key]), valB = parseVal(statsB[kingsSort.key]);
      if (valA < valB) return kingsSort.direction === 'asc' ? -1 : 1;
      if (valA > valB) return kingsSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortable;
  }, [curatedStats, kingsSort]);


  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-8 pb-12 h-full flex flex-col w-full">
       <div className="shrink-0 mb-2 sm:mb-4 px-2 sm:px-0">
         <h1 className={`text-2xl sm:text-3xl font-black ${themeClasses.textHead} flex items-center gap-3`}><ListFilter className="text-indigo-500" size={28}/> Market Screener</h1>
       </div>

       <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl flex-1 flex flex-col min-h-[400px] overflow-hidden w-full`}>
          
          <div className={`p-4 sm:p-6 border-b ${themeClasses.border} ${themeClasses.input} flex flex-col md:flex-row gap-4 items-start md:items-center shrink-0 w-full`}>
             <div className="relative flex-1 w-full md:max-w-md shrink-0">
               <Search size={16} className={`absolute left-4 top-3.5 ${themeClasses.textMuted}`} />
               <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Filter directory..." className={`w-full ${themeClasses.bgCard} border ${themeClasses.border} text-sm pl-12 pr-4 py-3 rounded-xl outline-none focus:border-indigo-500 ${themeClasses.textHead} font-bold`} />
             </div>
             
             <div className="flex flex-wrap gap-2 w-full md:w-auto">
                <button onClick={() => setActiveFilter('ALL')} className={`px-4 py-2 flex-1 md:flex-none justify-center rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${activeFilter === 'ALL' ? 'bg-indigo-500 text-white' : `${themeClasses.bgCard} ${themeClasses.textMuted} hover:text-indigo-400`}`}>All</button>
                <button onClick={() => {setActiveFilter('HOT'); setSortConfig({key: 'change_pct', direction: 'desc'});}} className={`px-4 py-2 flex-1 md:flex-none justify-center flex items-center gap-1 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${activeFilter === 'HOT' ? 'bg-emerald-500/20 text-emerald-500 ring-1 ring-emerald-500' : `${themeClasses.bgCard} ${themeClasses.textMuted} hover:text-emerald-400`}`} title="Momentum within 3% of 52W High"><TrendingUp size={14} className="hidden sm:block"/> Hot</button>
                <button onClick={() => {setActiveFilter('BAD'); setSortConfig({key: 'change_pct', direction: 'asc'});}} className={`px-4 py-2 flex-1 md:flex-none justify-center flex items-center gap-1 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${activeFilter === 'BAD' ? 'bg-red-500/20 text-red-500 ring-1 ring-red-500' : `${themeClasses.bgCard} ${themeClasses.textMuted} hover:text-red-400`}`} title="Bleeding > $10/share"><TrendingDown size={14} className="hidden sm:block"/> Bleeding</button>
                <button onClick={() => {setActiveFilter('VOLUME'); setSortConfig({key: 'volume', direction: 'desc'});}} className={`px-4 py-2 flex-1 md:flex-none justify-center flex items-center gap-1 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${activeFilter === 'VOLUME' ? 'bg-blue-500/20 text-blue-500 ring-1 ring-blue-500' : `${themeClasses.bgCard} ${themeClasses.textMuted} hover:text-blue-400`}`} title="> $100M Traded Today"><Activity size={14} className="hidden sm:block"/> High Vol</button>
             </div>
          </div>
          
          <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar p-0 w-full">
            {loading ? (
              <div className="flex items-center justify-center h-full flex-col gap-4">
                 <Loader2 className="animate-spin text-indigo-500" size={32}/>
                 <p className={`text-xs font-black uppercase tracking-widest ${themeClasses.textMuted}`}>Loading Market Snapshot...</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className={`sticky top-0 ${themeClasses.bgCard} z-10 shadow-sm`}>
                    <tr className={`${themeClasses.textMuted} uppercase text-[10px] tracking-widest border-b ${themeClasses.border}`}>
                      <th className="px-4 py-4 font-black cursor-pointer hover:text-indigo-500" onClick={() => requestSort('ticker')}>Ticker <ArrowUpDown size={10} className="inline ml-1"/></th>
                      <th className="px-4 py-4 font-black cursor-pointer hover:text-indigo-500" onClick={() => requestSort('name')}>Company <ArrowUpDown size={10} className="inline ml-1"/></th>
                      <th className="px-4 py-4 font-black text-right cursor-pointer hover:text-indigo-500" onClick={() => requestSort('fiftyTwoWeekHigh')}>52W High <ArrowUpDown size={10} className="inline ml-1"/></th>
                      <th className="px-4 py-4 font-black text-right cursor-pointer hover:text-indigo-500" onClick={() => requestSort('price')}>Price <ArrowUpDown size={10} className="inline ml-1"/></th>
                      <th className="px-4 py-4 font-black text-right cursor-pointer hover:text-indigo-500" onClick={() => requestSort('change_pct')}>Change <ArrowUpDown size={10} className="inline ml-1"/></th>
                      <th className="px-4 py-4 font-black text-right cursor-pointer hover:text-indigo-500" onClick={() => requestSort('volume')}>Volume <ArrowUpDown size={10} className="inline ml-1"/></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedTickers.map((t, i) => (
                      <tr 
                        key={i} 
                        onClick={() => { setGlobalSearchTicker(t.ticker); setCurrentRoute('dashboard'); }} 
                        className={`border-b ${themeClasses.border} hover:bg-indigo-500/5 transition-colors group cursor-pointer`}
                      >
                        <td className={`px-4 py-4 font-black text-indigo-500`}>{t.ticker}</td>
                        <td className={`px-4 py-4 font-bold ${themeClasses.textHead} group-hover:text-indigo-500 truncate max-w-[150px] sm:max-w-[200px]`}>{t.name || '---'}</td>
                        <td className={`px-4 py-4 text-right font-mono font-bold text-indigo-400`}>${(t.fiftyTwoWeekHigh || 0).toFixed(2)}</td>
                        <td className={`px-4 py-4 text-right font-mono font-bold ${themeClasses.textHead}`}>${(t.price || 0).toFixed(2)}</td>
                        <td className={`px-4 py-4 text-right font-mono font-bold ${t.change_pct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{t.change_pct >= 0 ? '+' : ''}{(t.change_pct || 0).toFixed(2)}%</td>
                        <td className={`px-4 py-4 text-right font-mono text-xs ${themeClasses.textMuted}`}>{(t.volume || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            )}
          </div>
       </div>

       <div className="shrink-0 mt-4 px-2 sm:px-0 w-full">
          <button 
            onClick={() => setShowCurated(!showCurated)}
            className={`w-full flex items-center justify-between p-4 rounded-2xl ${themeClasses.bgCard} border ${themeClasses.border} hover:border-orange-500/50 transition-colors cursor-pointer group shadow-sm`}
          >
            <h3 className={`text-xs font-black ${themeClasses.textHead} uppercase tracking-widest flex items-center gap-2 m-0`}>
              <Flame size={16} className="text-orange-500 group-hover:scale-110 transition-transform"/> Curated Dividend Yielders
            </h3>
            <div className={`text-[10px] font-bold ${themeClasses.textMuted} flex items-center gap-2 uppercase tracking-widest group-hover:text-orange-400 transition-colors`}>
              <span className="hidden sm:inline">{showCurated ? 'Hide Lists' : 'View Lists'}</span>
              {showCurated ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>

          {showCurated && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 animate-in fade-in slide-in-from-top-2 duration-200 w-full">
               
               <div className={`${themeClasses.bgCard} border ${themeClasses.border} rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col h-80 w-full overflow-hidden`}>
                 <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3 flex items-center justify-between shrink-0">
                   <span>👑 Dividend Kings ({DIVIDEND_KINGS.length})</span>
                   <span className={`text-[8px] ${themeClasses.textMuted} hidden sm:block`}>50+ YRS OF GROWTH</span>
                 </h4>
                 <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar w-full">
                    <table className="w-full text-left text-[10px] whitespace-nowrap min-w-[500px]">
                      <thead className={`sticky top-0 ${themeClasses.bgCard} z-10 shadow-sm border-b ${themeClasses.border}`}>
                        <tr className={`${themeClasses.textMuted} uppercase tracking-widest`}>
                          <th className="py-2 px-2 cursor-pointer hover:text-indigo-500" onClick={() => handleKingsSort('ticker')}>Ticker <ArrowUpDown size={8} className="inline ml-0.5"/></th>
                          
                          <th className="py-2 px-2 cursor-pointer hover:text-indigo-500" onClick={() => handleKingsSort('price')} title="Current Trading Price">
                            Price <ArrowUpDown size={8} className="inline ml-0.5"/>
                          </th>
                          
                          <th className="py-2 px-2 cursor-pointer hover:text-indigo-500" onClick={() => handleKingsSort('yield')} title="Annual cash return.&#10;✅ Low Risk Target: 2% - 5%.&#10;🚨 Danger: > 7% (High risk of a dividend cut / Yield Trap).">
                            Yield <span className="cursor-help border-b border-dotted border-slate-500">?</span> <ArrowUpDown size={8} className="inline ml-0.5"/>
                          </th>
                          
                          <th className="py-2 px-2 cursor-pointer hover:text-indigo-500" onClick={() => handleKingsSort('pe')} title="Price-to-Earnings.&#10;✅ Low Risk Target: 10 - 25.&#10;🚨 Danger: > 30 (Overvalued) or Negative (Losing money).">
                            P/E <span className="cursor-help border-b border-dotted border-slate-500">?</span> <ArrowUpDown size={8} className="inline ml-0.5"/>
                          </th>
                          
                          <th className="py-2 px-2 cursor-pointer hover:text-indigo-500" onClick={() => handleKingsSort('payout')} title="Percentage of profits paid to shareholders.&#10;✅ Low Risk Target: 30% - 60%.&#10;🚨 Danger: > 80% (Dividend is mathematically unsafe).">
                            Payout <span className="cursor-help border-b border-dotted border-slate-500">?</span> <ArrowUpDown size={8} className="inline ml-0.5"/>
                          </th>
                          
                          <th className="py-2 px-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedKings.map(ticker => (
                          <CuratedRow key={ticker} ticker={ticker} stats={curatedStats[ticker]} themeClasses={themeClasses} setCurrentRoute={setCurrentRoute} setGlobalSearchTicker={setGlobalSearchTicker} />
                        ))}
                      </tbody>
                    </table>
                 </div>
               </div>
               
               <div className={`${themeClasses.bgCard} border ${themeClasses.border} rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col h-80 w-full overflow-hidden`}>
                 <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-3 flex items-center justify-between shrink-0">
                   <span>📈 Benchmark Funds</span>
                   <span className={`text-[8px] ${themeClasses.textMuted} hidden sm:block`}>PASSIVE YIELD ETFS</span>
                 </h4>
                 <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar w-full">
                    <table className="w-full text-left text-[10px] whitespace-nowrap min-w-[400px]">
                      <thead className={`sticky top-0 ${themeClasses.bgCard} z-10 shadow-sm border-b ${themeClasses.border}`}>
                        <tr className={`${themeClasses.textMuted} uppercase tracking-widest`}>
                          <th className="py-2 px-2">Ticker</th>
                          <th className="py-2 px-2">Fund Name</th>
                          <th className="py-2 px-2" title="Current Trading Price">Price</th>
                          <th className="py-2 px-2" title="Annual cash return.&#10;✅ Low Risk Target: 2% - 5%.&#10;🚨 Danger: > 7% (High risk of a dividend cut / Yield Trap).">
                            Yield <span className="cursor-help border-b border-dotted border-slate-500">?</span>
                          </th>
                          <th className="py-2 px-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {HIGH_YIELD_ETFS.map(etf => (
                          <CuratedRow key={etf.ticker} ticker={etf.ticker} name={etf.name} isEtf={true} stats={curatedStats[etf.ticker]} themeClasses={themeClasses} setCurrentRoute={setCurrentRoute} setGlobalSearchTicker={setGlobalSearchTicker} />
                        ))}
                      </tbody>
                    </table>
                 </div>
               </div>
            </div>
          )}
       </div>
    </div>
  );
}